import { useEffect, useRef, useState, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';
import { ChevronDown, UserCircle, Bell, LogOut } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { supabase } from '@/lib/supabaseClient';
import type { UserRole } from '@/hooks/useUserRole';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin (관리자)',
  admin_low: 'Admin · Limited (제한 관리자)',
  bd_staff: 'BD Staff (BD 직원)',
  kr_staff: 'KR Staff (KR 직원)',
};

export function UserMenu({ user, role, unreadCount = 0 }: { user: User; role: UserRole; unreadCount?: number }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const displayName = (user.user_metadata?.full_name as string) || (user.user_metadata?.display_name as string) || user.email || '';
  const avatarUrl = user.user_metadata?.avatar_url as string | undefined;

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-xl hover:bg-surface-muted transition-colors duration-150 cursor-pointer"
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-700 to-primary-900 text-white shadow-pop flex items-center justify-center text-[12px] font-bold flex-shrink-0 overflow-hidden">
          {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : displayName.slice(0, 1).toUpperCase()}
        </div>
        <span className="text-[13px] font-semibold text-ink hidden sm:inline">{displayName}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-ink-faint transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl border border-border-subtle shadow-modal overflow-hidden z-50 animate-[popIn_0.15s_ease-premium]">
          <div className="px-4 py-3 border-b border-border-subtle">
            <p className="text-[10.5px] text-ink-faint">Signed in as (로그인 계정)</p>
            <p className="text-[13px] font-bold text-ink truncate">{user.email}</p>
            {role && (
              <div className="mt-1.5">
                <Badge tone="primary">{ROLE_LABEL[role] ?? role}</Badge>
              </div>
            )}
          </div>

          <div className="py-1">
            <Link
              to="/admin/profile"
              onClick={close}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-[12.5px] font-medium text-ink hover:bg-surface-muted cursor-pointer"
            >
              <UserCircle className="w-4 h-4 text-ink-faint" /> Profile Settings (프로필 설정)
            </Link>
            <Link
              to="/admin/notifications"
              onClick={close}
              className="w-full flex items-center justify-between px-4 py-2 text-[12.5px] font-medium text-ink hover:bg-surface-muted cursor-pointer"
            >
              <span className="flex items-center gap-2.5">
                <Bell className="w-4 h-4 text-ink-faint" /> Notifications (알림)
              </span>
              {unreadCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-error text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          </div>

          <div className="border-t border-border-subtle py-1">
            <button
              onClick={() => {
                close();
                supabase.auth.signOut();
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-[12.5px] font-semibold text-error hover:bg-error-bg cursor-pointer"
            >
              <LogOut className="w-4 h-4" /> Sign Out (로그아웃)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
